/**
 * Composer — поле ввода + кнопка отправки.
 *
 * Дизайн: pill-shape c sticky-focus-ring; кнопка Send круглая 36px справа
 * (как ChatGPT/Notion AI). Auto-grow textarea до 200px max.
 *
 * Keys:
 *   Enter         → отправка (одно-строчные виджеты, defaultSubmitOnEnter=true)
 *   Shift+Enter   → newline
 *   Ctrl/Cmd+Enter → отправка (длинные drawer'ы, defaultSubmitOnEnter=false)
 *
 * Props:
 *   value, onChange  — controlled input
 *   onSubmit         — (value) => void
 *   placeholder      — string
 *   disabled         — boolean (sending / quota dead)
 *   submitOnEnter    — boolean (true для widget, false для drawer)
 *   maxLength        — int (default 4000)
 *   hint             — string | null (под composer'ом, например "Ctrl+Enter — отправить")
 *
 * Auto-grow: меряем scrollHeight, выставляем height; max-height в CSS.
 */

import { useEffect, useRef } from 'react'
import { Send } from 'lucide-react'
import VoiceInput from './VoiceInput'

const MAX_HEIGHT_PX = 200

export default function Composer({
    value,
    onChange,
    onSubmit,
    placeholder = 'Спросить…',
    disabled = false,
    submitOnEnter = false,
    maxLength = 4000,
    hint = null,
    autoFocus = false,
    showVoice = false,
}) {
    const ref = useRef(null)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        el.style.height = 'auto'
        const next = Math.min(el.scrollHeight, MAX_HEIGHT_PX)
        el.style.height = `${next}px`
    }, [value])

    useEffect(() => {
        if (autoFocus && ref.current && !disabled) {
            ref.current.focus()
        }
    }, [autoFocus, disabled])

    const submit = () => {
        const text = (value || '').trim()
        if (!text || disabled) return
        onSubmit?.(text)
    }

    const onKeyDown = (e) => {
        if (e.key !== 'Enter') return
        if (submitOnEnter) {
            if (e.shiftKey) return // newline
            e.preventDefault()
            submit()
        } else {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault()
                submit()
            }
        }
    }

    const canSend = !disabled && Boolean((value || '').trim())

    const handleVoiceTranscript = (text) => {
        const current = (value || '').trimEnd()
        const next = current ? `${current} ${text}` : text
        onChange?.(next)
    }

    return (
        <div className="conversation__composer">
            <div className="conversation__composer-pill">
                <textarea
                    ref={ref}
                    className="conversation__composer-input"
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    rows={1}
                    maxLength={maxLength}
                    aria-label="Сообщение"
                />
                {showVoice && (
                    <VoiceInput
                        onTranscript={handleVoiceTranscript}
                        disabled={disabled}
                    />
                )}
                <button
                    type="button"
                    className="conversation__send-btn"
                    onClick={submit}
                    disabled={!canSend}
                    aria-label="Отправить"
                    title="Отправить"
                >
                    <Send size={16} />
                </button>
            </div>
            {hint ? <div className="conversation__composer-hint">{hint}</div> : null}
        </div>
    )
}
